import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';

/**
 * Configure Amplify for the real FoloLabs Cognito User Pool.
 *
 * Two sign-in modes, picked by env:
 *   - **Hosted UI (OAuth redirect)** — when `VITE_COGNITO_DOMAIN` is set.
 *     This is production SSO: one login at the Cognito Hosted UI, session
 *     shared across *.fololabs.io via the cookie. `signInWithRedirect()`.
 *   - **SRP email/password** — fallback when no domain (the dev pool has no
 *     Hosted UI). In-app form, `USER_SRP_AUTH`.
 *
 * Env (baked at build time by CF Pages):
 *   VITE_COGNITO_USER_POOL_ID         e.g. us-east-1_aR4qaCncs
 *   VITE_COGNITO_USER_POOL_CLIENT_ID  e.g. 7bm52k9hn3qs2cgmv8frsmch5f
 *   VITE_COGNITO_REGION               e.g. us-east-1
 *   VITE_COGNITO_DOMAIN               e.g. auth.fololabs.io  (enables Hosted UI)
 *   VITE_COOKIE_DOMAIN                e.g. .fololabs.io      (cross-subdomain SSO)
 *   VITE_OAUTH_REDIRECT               e.g. https://app.fololabs.io/  (defaults to origin)
 */
export interface AmplifyConfigResult {
  configured: boolean;
  hostedUi: boolean;
}

export function configureAmplify(): AmplifyConfigResult {
  const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as
    | string
    | undefined;
  const userPoolClientId = import.meta.env
    .VITE_COGNITO_USER_POOL_CLIENT_ID as string | undefined;
  const region = import.meta.env.VITE_COGNITO_REGION as string | undefined;
  const cookieDomain = import.meta.env.VITE_COOKIE_DOMAIN as string | undefined;
  const hostedUiDomain = import.meta.env.VITE_COGNITO_DOMAIN as
    | string
    | undefined;

  if (!userPoolId || !userPoolClientId || !region) {
    // Real Cognito params absent — mock mode handles auth instead.
    return { configured: false, hostedUi: false };
  }

  const hostedUi = Boolean(hostedUiDomain);
  const redirect =
    (import.meta.env.VITE_OAUTH_REDIRECT as string | undefined) ??
    (typeof window !== 'undefined' ? `${window.location.origin}/` : '/');

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
        loginWith: hostedUi
          ? {
              oauth: {
                domain: hostedUiDomain as string,
                scopes: ['openid', 'email', 'profile'],
                redirectSignIn: [redirect],
                redirectSignOut: [redirect],
                responseType: 'code',
              },
            }
          : // SRP — matches the pool client (`userSrp: true`). Email is the username.
            { username: false, email: true, phone: false },
      },
    },
  });

  // Share the session across *.fololabs.io for single sign-on.
  if (cookieDomain) {
    cognitoUserPoolsTokenProvider.setKeyValueStorage(
      new CookieStorage({
        domain: cookieDomain,
        path: '/',
        expires: 30,
        secure: true,
        sameSite: 'lax',
      }),
    );
  }

  return { configured: true, hostedUi };
}
